import { esportaXLSX } from "../utils/esportaXLSX";
import React, { useEffect, useState, useMemo } from "react";
import { collection, query, where, onSnapshot, doc, getDoc, setDoc } from "firebase/firestore";
import { db } from "../firebase";
import { useAuth } from "../contexts/AuthContext";
import { useOrg } from "../contexts/OrgContext";
import { FileText, Search, ChevronLeft, ChevronRight, Eye, Users, X } from "lucide-react";
import { EmptyState } from "../utils/EmptyState";
import { StatusBadge, normalizzaStato } from "../utils/StatusBadge";
import { descrizioneSede } from "../utils/sediCliente";
import { apriDocumentoRapportino } from "../components/RapportiniTab";
import { useNav } from "../contexts/NavContext";

export default function RegistroRapportiniPage() {
  const { currentUser, isOrgAdmin, isPlatformOwner } = useAuth();
  const { effectiveOrgId, orgVisitata } = useOrg();
  const { navigateFull } = useNav();
  // Un clic sul rapportino apre il documento vero (lo stesso di «Stampa/PDF» nella commessa);
  // un clic sulla commessa apre la commessa, nella scheda Rapportini, dove si modifica.
  const apri = r => apriDocumentoRapportino(r, orgVisitata || {}, r.projectName || "");
  const vaiAllaCommessa = (e, r) => { e.stopPropagation(); if (r.projectId) navigateFull({ page: "project", projectId: r.projectId, initialTab: "rapportini" }); };
  const canAdmin = isOrgAdmin || isPlatformOwner;

  const [rapportini, setRapportini] = useState([]);
  const [utenti, setUtenti] = useState([]);
  const [filtroTesto, setFiltroTesto] = useState("");
  const [filtroAnno, setFiltroAnno] = useState(new Date().getFullYear());
  const [filtroCommessa, setFiltroCommessa] = useState("tutte");
  const [vistaMode, setVistaMode] = useState("commessa"); // "commessa" | "cronologica"



  useEffect(()=>{
    if(!effectiveOrgId) return;
    return onSnapshot(query(collection(db,"users"),where("organizationId","==",effectiveOrgId),where("enabled","==",true)),
      snap=>setUtenti(snap.docs.map(d=>d.data())));
  },[effectiveOrgId]);

  // Determina se utente corrente può vedere il registro

  useEffect(()=>{
    if(!effectiveOrgId) return;
    return onSnapshot(query(collection(db,"rapportini"),where("orgId","==",effectiveOrgId)),
      snap=>setRapportini(snap.docs.map(d=>({id:d.id,...d.data()})).sort((a,b)=>(b.data||"").localeCompare(a.data||"") || (b.numero||"").localeCompare(a.numero||""))));
  },[effectiveOrgId]);

  // Anno del rapportino: dalla data dell'intervento; se manca, dal numero.
  // Il numero può essere «INT-2026-0187» (attuale), «2026-001» o «2026_11-26_01» (vecchi):
  // si cerca il primo anno a 4 cifre, così nessun formato resta escluso dal registro.
  const estraiAnno = (r) => {
    if (r?.data && /^\d{4}/.test(r.data)) return r.data.slice(0, 4);
    const m = String(r?.numero || "").match(/(?:^|\D)((?:19|20)\d{2})(?!\d)/);
    return m ? m[1] : null;
  };

  const anni = useMemo(()=>{
    const s = new Set(rapportini.map(r=>estraiAnno(r)).filter(Boolean));
    return [...s].sort().reverse();
  },[rapportini]);

  const visibili = useMemo(()=>{
    return rapportini.filter(r=>{
      const annoR = estraiAnno(r);
      if(annoR && String(annoR)!==String(filtroAnno)) return false;
      if(filtroCommessa!=="tutte" && (r.projectName||"")!==filtroCommessa) return false;
      if(filtroTesto){
        const q = filtroTesto.toLowerCase();
        return (r.numero||"").toLowerCase().includes(q) ||
               (r.nomeCliente||"").toLowerCase().includes(q) ||
               (descrizioneSede(r.sede)||r.sitoOggetto||"").toLowerCase().includes(q) ||
               (r.descrizioneBreve||r.lavorazioni||"").toLowerCase().includes(q);
      }
      return true;
    });
  },[rapportini,filtroAnno,filtroTesto,filtroCommessa]);

  // Vista cronologica — ordinata per data decrescente
  const visibiliCronologici = useMemo(()=>{
    return [...visibili].sort((a,b)=>{
      const da = a.data ? new Date(a.data) : new Date(0);
      const db = b.data ? new Date(b.data) : new Date(0);
      return db - da;
    });
  },[visibili]);

  const commesseDisponibili = useMemo(()=>{
    return [...new Set(rapportini.map(r=>r.projectName).filter(Boolean))].sort();
  },[rapportini]);

  // Raggruppamento per cliente
  const perCliente = useMemo(()=>{
    const map = {};
    visibili.forEach(r=>{
      const key = r.nomeCliente || "— Senza cliente —";
      if(!map[key]) map[key] = [];
      map[key].push(r);
    });
    return Object.entries(map).sort((a,b)=>a[0].localeCompare(b[0],"it"));
  },[visibili]);




  return (
    <div style={{display:"flex",flexDirection:"column",height:"100%",overflow:"hidden"}}>
      {/* Header */}
      <div style={{background:"#fff",borderBottom:"1px solid #e5e7eb",padding:"14px 20px",flexShrink:0}}>
        <div style={{display:"flex",alignItems:"center",gap:14,marginBottom:12}}>
          <h2 style={{fontSize:16,fontWeight:800,display:"flex",alignItems:"center",gap:8}}>
            <FileText size={17} color="#7c3aed"/> Registro Rapportini

          </h2>
            <button onClick={()=>{
              const intestazione=["Numero","Data","Commessa","Cliente","Tipologia","Motivo","Esito","Compilatore / operatori"];
              const righe=(visibiliCronologici||[]).map(r=>[ r.numero||"—",r.data?new Date(r.data+"T00:00:00").toLocaleDateString("it-IT"):"—",r.projectName||"—",r.nomeCliente||r.clienteNome||"—",r.tipologiaIntervento||r.tipo||"—",r.motivoIntervento||r.descrizioneBreve||r.oggetto||"—",r.esitoIntervento||r.esito||"—",r.compilatoreRegistro||(r.operatori||[]).filter(Boolean).join(", ")||r.compilatoreNome||"—"]);
              esportaXLSX([intestazione,...righe],`Registro_Rapportini_${new Date().toLocaleDateString("it-IT").replace(/\//g,"-")}`,"Rapportini");
            }} style={{display:"flex",alignItems:"center",gap:5,padding:"6px 12px",border:"1px solid #059669",borderRadius:7,background:"#f0fdf4",color:"#059669",fontSize:12,fontWeight:700,cursor:"pointer",fontFamily:"'Inter',sans-serif"}}>
              📊 Excel
            </button>
        </div>

        {/* Filtri */}
        <div style={{display:"flex",gap:8,flexWrap:"wrap",alignItems:"center"}}>
          <div style={{display:"flex",alignItems:"center",gap:4,background:"#f9fafb",border:"1px solid #e5e7eb",borderRadius:8,padding:"5px 10px"}}>
            <Search size={13} color="#9ca3af"/>
            <input value={filtroTesto} onChange={e=>setFiltroTesto(e.target.value)}
              placeholder="Cerca numero, cliente, sito..."
              style={{border:"none",background:"transparent",fontSize:12,outline:"none",fontFamily:"'Inter',sans-serif",width:220}}/>
          </div>
          <div style={{display:"flex",alignItems:"center",gap:4}}>
            <button onClick={()=>setFiltroAnno(a=>a-1)} style={{border:"1px solid #e5e7eb",background:"#fff",borderRadius:6,padding:"4px 8px",cursor:"pointer"}}><ChevronLeft size={13}/></button>
            <span style={{fontSize:13,fontWeight:700,minWidth:50,textAlign:"center"}}>{filtroAnno}</span>
            <button onClick={()=>setFiltroAnno(a=>a+1)} style={{border:"1px solid #e5e7eb",background:"#fff",borderRadius:6,padding:"4px 8px",cursor:"pointer"}}><ChevronRight size={13}/></button>
          </div>
          
          {/* Toggle vista */}
          <div style={{display:"flex",background:"#f3f4f6",borderRadius:7,border:"1px solid #e5e7eb",marginLeft:8}}>
            {[{id:"commessa",label:"Per cliente"},{id:"cronologica",label:"Cronologica"}].map(v=>(
              <button key={v.id} onClick={()=>setVistaMode(v.id)}
                style={{padding:"5px 12px",border:"none",borderRadius:7,
                  background:vistaMode===v.id?"#7c3aed":"transparent",
                  color:vistaMode===v.id?"#fff":"#6b7280",fontSize:11,fontWeight:vistaMode===v.id?700:400,
                  cursor:"pointer",fontFamily:"'Inter',sans-serif"}}>
                {v.label}
              </button>
            ))}
          </div>

          <span style={{fontSize:12,color:"#9ca3af"}}>{visibili.length} rapportino/i</span>
        </div>
      </div>

      {/* Tabella registro */}
      <div style={{flex:1,overflowY:"auto"}}>
        {/* Vista per cliente — righe ordinate per cliente, nessun separatore */}
        {vistaMode==="commessa" && (
          <table style={{borderCollapse:"collapse",width:"100%",fontSize:12}}>
            <thead style={{position:"sticky",top:0,zIndex:5}}>
              <tr style={{background:"#1e1b4b"}}>
                <th style={{padding:"8px 12px",textAlign:"left",color:"#fff",fontSize:10,fontWeight:700,width:160}}>N° Rapportino</th>
                <th style={{padding:"8px 12px",textAlign:"left",color:"#fff",fontSize:10,fontWeight:700,width:90}}>Data</th>
                <th style={{padding:"8px 12px",textAlign:"left",color:"#fff",fontSize:10,fontWeight:700,width:160}}>Cliente</th>
                <th style={{padding:"8px 12px",textAlign:"left",color:"#fff",fontSize:10,fontWeight:700,width:130}}>Commessa</th>
                <th style={{padding:"8px 12px",textAlign:"left",color:"#fff",fontSize:10,fontWeight:700,width:120}}>Sito</th>
                <th style={{padding:"8px 12px",textAlign:"left",color:"#fff",fontSize:10,fontWeight:700}}>Descrizione</th>
                
              </tr>
            </thead>
            <tbody>
              {visibili.length===0 && (
                <tr><td colSpan={6}><EmptyState tipo="documenti" titolo="Nessun rapportino trovato" descrizione="I rapportini emessi appariranno qui."/></td></tr>
              )}
              {perCliente.map(([cliente, righe])=>(
                righe.map((r,i)=>{
                  const descrizione = r.descrizioneBreve || (r.lavorazioni||"").slice(0,100);
                  return (
                    <tr key={r.id} onClick={()=>apri(r)} title="Apri il rapportino"
                      style={{background:i%2===0?"#fff":"#fafafa",borderBottom:"1px solid #f3f4f6",cursor:"pointer",transition:"background .1s"}}
                      onMouseOver={e=>e.currentTarget.style.background="#faf5ff"}
                      onMouseOut={e=>e.currentTarget.style.background=i%2===0?"#fff":"#fafafa"}>
                      <td style={{padding:"8px 12px",fontWeight:800,color:"#5b21b6",fontFamily:"monospace",fontSize:13,whiteSpace:"nowrap"}}>{r.numero||"—"}</td>
                      <td style={{padding:"8px 12px",color:"#374151",whiteSpace:"nowrap"}}>{r.data?new Date(r.data+"T00:00:00").toLocaleDateString("it-IT",{day:"2-digit",month:"2-digit",year:"numeric"}):"—"}</td>
                      <td style={{padding:"8px 12px"}}><span style={{fontWeight:700,color:"#111827"}}>{r.nomeCliente||"—"}</span></td>
                      <td onClick={e=>vaiAllaCommessa(e,r)} title={r.projectId?"Apri la commessa":undefined} style={{padding:"8px 12px",color:"#7c3aed",fontWeight:600,fontSize:11,textDecoration:r.projectId?"underline":"none",textUnderlineOffset:2}}>{r.projectName?<>{r.codiceCommessa&&<span style={{fontFamily:"monospace",marginRight:4}}>[{r.codiceCommessa}]</span>}{r.projectName}</>:<span style={{color:"#d1d5db",fontSize:10}}>n.d.</span>}</td>
                      <td style={{padding:"8px 12px",color:"#6b7280",fontSize:11}}>{descrizioneSede(r.sede)||r.sitoOggetto||"—"}</td>
                      <td style={{padding:"8px 12px",color:"#374151",maxWidth:260,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{descrizione||"—"}</td>
                      
                    </tr>
                  );
                })
              ))}
            </tbody>
          </table>
        )}
        {/* Vista cronologica */}
        {vistaMode==="cronologica" && (
        <table style={{borderCollapse:"collapse",width:"100%",fontSize:12}}>
          <thead style={{position:"sticky",top:0,zIndex:5}}>
            <tr style={{background:"#1e1b4b"}}>
              <th style={{padding:"8px 12px",textAlign:"left",color:"#fff",fontSize:10,fontWeight:700,width:160}}>N° Rapportino</th>
              <th style={{padding:"8px 12px",textAlign:"left",color:"#fff",fontSize:10,fontWeight:700,width:90}}>Data</th>
              <th style={{padding:"8px 12px",textAlign:"left",color:"#fff",fontSize:10,fontWeight:700,width:160}}>Cliente</th>
              <th style={{padding:"8px 12px",textAlign:"left",color:"#fff",fontSize:10,fontWeight:700,width:130}}>Commessa</th>
              <th style={{padding:"8px 12px",textAlign:"left",color:"#fff",fontSize:10,fontWeight:700,width:120}}>Sito</th>
              <th style={{padding:"8px 12px",textAlign:"left",color:"#fff",fontSize:10,fontWeight:700}}>Descrizione</th>
              <th style={{padding:"8px 12px",textAlign:"center",color:"#fff",fontSize:10,fontWeight:700,width:60}}>Firme</th>
            </tr>
          </thead>
          <tbody>
            {visibiliCronologici.length===0 && (
<tr><td colSpan={7}><EmptyState tipo="documenti" titolo="Nessun rapportino trovato" descrizione="I rapportini emessi appariranno qui."/></td></tr>
            )}
            {visibiliCronologici.map((r,i)=>{
              const descrizione = r.descrizioneBreve || (r.lavorazioni||"").slice(0,100);
              return (
                <tr key={r.id}
                  onClick={()=>apri(r)} title="Apri il rapportino"
                  style={{background:i%2===0?"#fff":"#fafafa",borderBottom:"1px solid #f3f4f6",cursor:"pointer",transition:"background .1s"}}
                  onMouseOver={e=>e.currentTarget.style.background="#faf5ff"}
                  onMouseOut={e=>e.currentTarget.style.background=i%2===0?"#fff":"#fafafa"}>
                  <td style={{padding:"8px 12px",fontWeight:800,color:"#5b21b6",fontFamily:"monospace",fontSize:13,whiteSpace:"nowrap"}}>
                    {r.numero||"—"}
                  </td>
                  <td style={{padding:"8px 12px",color:"#374151",whiteSpace:"nowrap"}}>
                    {r.data ? new Date(r.data+"T00:00:00").toLocaleDateString("it-IT",{day:"2-digit",month:"2-digit",year:"numeric"}) : "—"}
                  </td>
                  <td style={{padding:"8px 12px"}}>
                    <span style={{fontWeight:700,color:"#111827"}}>{r.nomeCliente||"—"}</span>
                  </td>
                  <td onClick={e=>vaiAllaCommessa(e,r)} title={r.projectId?"Apri la commessa":undefined} style={{padding:"8px 12px",color:"#7c3aed",fontWeight:600,fontSize:11,textDecoration:r.projectId?"underline":"none",textUnderlineOffset:2}}>
                    {r.projectName || <span style={{color:"#d1d5db",fontSize:10}}>n.d.</span>}
                  </td>
                  <td style={{padding:"8px 12px",color:"#6b7280",fontSize:11}}>{descrizioneSede(r.sede)||r.sitoOggetto||"—"}</td>
                  <td style={{padding:"8px 12px",color:"#374151",fontSize:11}}>
                    {descrizione ? (descrizione.length>100 ? descrizione.slice(0,100)+"..." : descrizione) : "—"}
                  </td>
                  <td style={{padding:"8px 12px",textAlign:"center"}}>
                    {(r.firmaCliente || r.firmaTecnico) ? <span style={{color:"#059669",fontSize:14}}>✍️</span> : <span style={{color:"#d1d5db",fontSize:12}}>—</span>}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        )}
      </div>



    </div>
  );
}
